import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Button, Modal, ModalHeader, ModalTitle, ModalBody, ModalDivider, ModalFooter, Typography, Alert } from '@qtm/react2';
import { useMsal } from '@azure/msal-react';

const PdfViewer = forwardRef((_, ref) => {
  const { instance, accounts } = useMsal(); // MSAL for authentication and authorization
  const [pdfOpen, setPdfOpen] = useState(false); // State to control modal visibility
  const [pdfHref, setPdfHref] = useState(''); // State to hold current PDF URL
  const [pdfStatus, setPdfStatus] = useState('idle'); // PDF loading status
  const [pdfError, setPdfError] = useState(''); // State to hold any error messages
  const [pdfObjectUrl, setPdfObjectUrl] = useState(''); // Stores the object URL for displayed PDF
  const [pdfName, setPdfName] = useState(''); // Hold the PDF name for display

  // Convert a given URL to a SharePoint sharing token
  const toSharingTokenUrl = useCallback((rawUrl) => {
    try {
      const base = typeof rawUrl === 'string' ? rawUrl.trim() : '';
      if (!base) return null;
      const b64 = btoa(unescape(encodeURIComponent(base)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
      return `u!${b64}`;
    } catch (_) {
      return null;
    }
  }, []);

  // Get an access token using MSAL
  const getGraphToken = useCallback(async () => {
    const account = (accounts && accounts.length > 0) ? accounts[0] : undefined;
    const request = { scopes: ['Files.Read', 'Sites.Read.All'], account };
    try {
      const resp = await instance.acquireTokenSilent(request);
      return resp.accessToken;
    } catch (e) {
      const resp = await instance.acquireTokenPopup(request);
      return resp.accessToken;
    }
  }, [instance, accounts]);

  // Open PDF modal given a PDF URL
  const openPdfModal = useCallback(async (href) => {
    try {
      setPdfHref(href); // Set PDF URL in state
      setPdfOpen(true); // Open the modal
      setPdfStatus('loading'); // Set loading status
      setPdfError(''); // Clear any previous errors
      setPdfName(href.split('/').pop() || 'Document.pdf'); // Derive and set the PDF name
      const token = await getGraphToken(); // Get an access token for Graph API
      const sharing = toSharingTokenUrl(href); // Convert URL to SharePoint sharing token
      if (!sharing) throw new Error('Invalid SharePoint URL');
      const api = `https://graph.microsoft.com/v1.0/shares/${sharing}/driveItem/content`;
      
      // Fetch PDF data using Graph API
      const resp = await fetch(api, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`Graph download failed: ${resp.status}`);
      const blob = await resp.blob(); // Convert response to blob

      if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl); // Revoke previous object URL
      const obj = URL.createObjectURL(blob); // Create a new object URL for the blob
      setPdfObjectUrl(obj); // Save the developed object URL
      setPdfStatus('ready'); // Update status to indicate PDF is ready
    } catch (e) {
      setPdfStatus('error'); // Set error status
      setPdfError(e?.message || 'Failed to load PDF'); // Save error message in state
    }
  }, [getGraphToken, toSharingTokenUrl, pdfObjectUrl]);

  // Close the PDF modal
  const closePdfModal = useCallback(() => {
    setPdfOpen(false); // Close the modal
    if (pdfObjectUrl) {
      URL.revokeObjectURL(pdfObjectUrl); // Revoke object URL to clean up resources
      setPdfObjectUrl(''); // Clear object URL from state
    }
    setPdfHref(''); // Clear PDF URL state
    setPdfStatus('idle'); // Reset status for potential future uses
    setPdfError(''); // Clear error messages
  }, [pdfObjectUrl]);

  // Expose the open and close functions via ref
  useImperativeHandle(ref, () => ({
    openPdfModal,
    closePdfModal,
  }), [openPdfModal, closePdfModal]);

  return (
    <Modal open={pdfOpen} classes="pdf-modal" onCloseModal={closePdfModal}>
      <ModalHeader>
        <ModalTitle>
          <Typography variant="heading-6">{pdfName || 'PDF Preview'}</Typography>
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        {pdfStatus === 'loading' && <div>Loading PDF…</div>}
        {pdfStatus === 'error' && <Alert variant="warning">{pdfError}</Alert>}
        {pdfObjectUrl && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden', width: '90vw' }}>
            <iframe title="PDF" src={pdfObjectUrl} style={{ width: '100%', height: '82vh', border: 'none' }} />
          </div>
        )}
      </ModalBody>
      <ModalDivider />
      <ModalFooter classes="justify-end">
        <Button
          variant="outline"
          color="primary"
          onClickEvent={() => {
            if (pdfHref) window.open(pdfHref, '_blank', 'noopener');
          }}
        >
          Open in new tab
        </Button>
        <Button variant="outline" color="primary" onClickEvent={closePdfModal}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
});

export default PdfViewer;